import { Component, inject, OnInit } from '@angular/core';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import {
  AbstractControl,
  FormControl,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  ValidatorFn,
  Validators,
} from '@angular/forms';
import { LoginService } from '../../../core/api/services/login/login.service';
import { Router, RouterLink, ActivatedRoute } from '@angular/router';
import { NgClass } from '@angular/common';
import { CommonService } from '../../../core/api/services/utility-services/common.service';
import { UrlConstants } from '../../../core/constants/url.constants';
import { GenericStateService } from '../../../core/api/services/utility-services/generic-state.service';
import { AppFooterComponent } from '../../common/app-footer/app-footer.component';
import { SessionStorageConstants } from '../../../core/constants/session-storage.constant';
import { EnvironmentService } from '../../../../environments/services/environment.service';
import { requiredTrimmed } from '../../../core/util/custom-form-validators-util';
import { LocaleService } from '../../../core/services/locale.service';
import { CrcLocale } from '../../../core/models/locale/crc-locale';
import { EventTypeConstants } from '../../../core/constants/event-type.constants';
import { PasswordInputComponent } from '../../common/password-input/password-input.component';
import { StandardFormFieldComponent } from '../../common/standard-form-field/standard-form-field.component';
import { TimeoutService } from '../../../core/api/services/utility-services/timeout.service';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

@Component({
  selector: 'app-login',
  imports: [
    TranslatePipe,
    FormsModule,
    ReactiveFormsModule,
    NgClass,
    RouterLink,
    PasswordInputComponent,
    StandardFormFieldComponent,
  ],
  templateUrl: './login.component.html',
  styleUrls: ['./login.component.css'],
})
export class LoginComponent implements OnInit {
  private readonly destroy$ = new Subject<void>();

  readonly loginService = inject(LoginService);
  readonly translateService = inject(TranslateService);
  readonly commonService = inject(CommonService);
  readonly router = inject(Router);
  readonly activatedRoute = inject(ActivatedRoute);
  readonly UrlConstants = UrlConstants;
  readonly genericStateService = inject(GenericStateService);
  readonly environmentService = inject(EnvironmentService);
  readonly localeService = inject(LocaleService);
  readonly timeoutService = inject(TimeoutService);

  userNameEmptyError = false;
  passwordEmptyError = false;
  currentLocale: CrcLocale | undefined;
  allLocales: CrcLocale[];

  errors: string[] = [];
  supportMail: string;
  loading: boolean = false;
  loginForm: FormGroup = new FormGroup({
    username: new FormControl("", requiredTrimmed()),
    password: new FormControl("", [Validators.required]),
  });
  fieldTextType: boolean = false;
  toggleFieldTextType() {
    this.fieldTextType = !this.fieldTextType;
  }

  ngOnInit() {
    const samlError = this.activatedRoute.snapshot.queryParams['samlError'];
    if (samlError) {
      const VALID_SAML_ERRORS = new Set([
        'validationFailed', 'ambiguousMatch', 'configurationError', 'cppUnavailable'
      ]);
      const safeKey = VALID_SAML_ERRORS.has(samlError) ? samlError : 'somethingWentWrong';
      this.errors.push(this.translateService.instant('login.error.message.' + safeKey));
    }
    this.genericStateService.clearData();
    this.supportMail = this.getSupportMail();
    this.loading = false;
    this.currentLocale = this.localeService.getLocale();
    this.allLocales = this.localeService.LOCALES;
    this.timeoutService.stopTimer();
    this.loginForm
      .get('username')
      ?.valueChanges.pipe(takeUntil(this.destroy$))
      .subscribe(() => this.clearFieldError('username'));

    this.loginForm
      .get('password')
      ?.valueChanges.pipe(takeUntil(this.destroy$))
      .subscribe(() => this.clearFieldError('password'));
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  clearFieldError(controlName: string) {
    this.errors = (this.errors || []).filter((err) => {
      try {
        return !err.toLowerCase().includes(controlName);
      } catch {
        return true;
      }
    });
    const ctrl = this.loginForm.get(controlName);
    if (ctrl) {
      ctrl.setErrors(null);
    }
  }

  getSupportMail() {
    switch (this.environmentService.environment) {
      case 'localhost':
        return SessionStorageConstants.CRC_LOCAL_SUPPORT_MAIL;
      case 'development':
        return SessionStorageConstants.CRC_DEV_SUPPORT_MAIL;
      case 'staging':
        return SessionStorageConstants.CRC_STAGE_SUPPORT_MAIL;
      case 'production':
        return SessionStorageConstants.CRC_PROD_SUPPORT_MAIL;
      default:
        return SessionStorageConstants.CRC_LOCAL_SUPPORT_MAIL;
    }
  }

  selectLocale(localeId: number) {
    const locale = this.localeService.getLocaleForId(localeId);
    if (locale && locale != this.localeService.getLocaleStr()) {
      this.localeService.setLocale(locale);
      window.location.reload();
    }
  }

  submit() {
    this.errors = [];
    this.loginForm.get('username')?.markAsDirty();
    this.loginForm.get('password')?.markAsDirty();
    if (this.loginForm.valid) {
      this.loading = true;
      this.loginForm.patchValue({ username: this.loginForm.value.username.trim() });
      this.loginService.login(this.loginForm.value).subscribe(
          (response) => {
              this.loginForm.get("password")?.setValue("");
              this.loginForm.get("password")?.markAsPristine();
              if (response.redirectUrl.includes("login")) {
                  this.loading = false;
                  if (response.errMsg.includes("accountLocked")) {
                      this.errors.push(
                          this.translateService.instant("login." + response.errMsg, { 0: this.supportMail })
                      );
                  } else if (response.errMsg.includes("&")) {
                      let errors = response.errMsg.split("&");
                      this.errors.push(this.translateService.instant("login." + errors[0].trim()));
                      if (Number(errors[2]) > 0) this.errors.push(this.translateService.instant("login." + errors[1].trim(), { 0: errors[2] }));
                      if (Number(errors[2]) === 0) this.errors.push(this.translateService.instant("login.error.message.accountLocked", { 0: this.supportMail }));
                  } else {
                      this.errors.push(this.translateService.instant("login." + response.errMsg));
                  }
              } else {
                  this.loading = false;
                  this.commonService.session().subscribe(() => {
                      this.commonService.logEvent(EventTypeConstants.LOGIN, "User sucessfully logged in");
                      this.timeoutService.restartTimer();
                      this.router.navigateByUrl(response.redirectUrl);
                  });
              }
          },
          (error) => {
              this.loading = false;
              this.loginForm.get("password")?.setValue("");
              if (error.error?.errMsg.includes("&")) {
                  let errors = error.error.errMsg.split("&");
                  this.errors.push(this.translateService.instant("login." + errors[0].trim()));
                  if (Number(errors[2]) > 0) this.errors.push(this.translateService.instant("login." + errors[1].trim(), { 0: errors[2] }));
                  if (Number(errors[2]) === 0) this.errors.push(this.translateService.instant("login.error.message.accountLocked", { 0: this.supportMail }));
              } else if(error.error?.errMsg.includes("accountLocked")) {
                  this.errors.push(this.translateService.instant("login." + error.error?.errMsg, { 0: this.supportMail }));
              } else {
                  this.errors.push(this.translateService.instant("login." + error.error?.errMsg));
              }
          }
      );
    } else {
      if (!this.loginForm.get('username')?.valid) {
        const userName = this.translateService.instant('login.login.box.username')
        this.errors.push(this.translateService.instant('login.login.validation.empty', { 0: userName }))
        this.userNameEmptyError = true;
      }
      if (!this.loginForm.value.password) {
        const password = this.translateService.instant('login.login.box.pswd')
        this.errors.push(this.translateService.instant("login.login.validation.empty", { 0: password }))
        this.passwordEmptyError = true;
      }
    }
  }
}
